/**
 * Utility for converting Supabase mock refs to real Firestore DocumentReferences.
 *
 * When data is loaded from Supabase, items have "mock" ref objects that look like
 * Firestore refs but aren't real DocumentReferences. This utility converts them
 * back to real Firestore refs for dual-write operations.
 */

import { getDefaultStore } from 'jotai'
import { organizationAtom } from '../AtomsProvider'
import { rootDb } from './firebaseFirestore'
import { snapshotExists } from './types'
import type { Organization, MockRef } from './types'

// ============================================================================
// Types
// ============================================================================

/**
 * Item that may have a Firestore ref or Supabase mock ref
 */
export interface ItemWithRef {
  ref?: MockRef | unknown
  firestoreId?: string
  id?: string
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Root-level collections that don't need organization scoping.
 * All other collections are nested under organizations/{orgId}/
 */
const ROOT_LEVEL_COLLECTIONS = ['users', 'organizations']

// ============================================================================
// Main Function
// ============================================================================

/**
 * Convert a Supabase mock ref or item to a real Firestore DocumentReference.
 *
 * Handles multiple input formats:
 * 1. Direct Supabase mock ref (has _isSupabaseMock flag)
 * 2. Item object with ref property (ref may be mock or real)
 * 3. Item object with firestoreId/id property
 * 4. Already a real Firestore ref (passes through)
 *
 * For nested collections (products, sales, stores, etc.), constructs the
 * organization-scoped path: organizations/{orgId}/{collection}/{docId}
 *
 * @param item - The item object or ref to convert
 * @param collectionPath - The Firestore collection path (e.g., 'stores', 'products')
 * @returns The real Firestore document reference
 * @throws Error if no valid id can be found
 *
 * @example
 * // RECOMMENDED: Pass the whole item object (works even when .ref is undefined)
 * const ref = getFirestoreRef(product, 'products')
 *
 * // AVOID: Passing .ref directly may fail if ref was stripped during Firestore save
 * // const ref = getFirestoreRef(product.ref, 'products')  // May throw if product.ref is undefined
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Refs can be Firestore or mock objects with dynamic properties
export const getFirestoreRef = (item: any, collectionPath = 'products'): any => {
  // Guard against null/undefined items - provide a helpful error message
  if (item == null) {
    throw new Error(
      `Cannot resolve Firestore ref for collection '${collectionPath}': item is ${item}. ` +
      `This usually happens when passing 'item.ref' instead of 'item' - pass the whole object so the id can be extracted.`
    )
  }

  /**
   * Helper to build the correct Firestore ref based on collection type
   */
  const buildFirestoreRef = (docId: string): unknown => {
    if (ROOT_LEVEL_COLLECTIONS.includes(collectionPath)) {
      return rootDb.collection(collectionPath).doc(docId)
    }
    // Nested collections need organization scoping
    const jotaiStore = getDefaultStore()
    const organization = jotaiStore.get(organizationAtom) as (Organization & { firestoreId?: string }) | null
    const orgFirestoreId = organization?.firestoreId || organization?.id
    if (orgFirestoreId) {
      return rootDb.collection('organizations').doc(orgFirestoreId).collection(collectionPath).doc(docId)
    }
    // Fallback to root-level if no organization (shouldn't happen for nested collections)
    console.warn(`No organization found for nested collection '${collectionPath}', using root-level path`)
    return rootDb.collection(collectionPath).doc(docId)
  }

  // Handle case where a direct ref is passed (backward compatibility)
  if (item._isSupabaseMock) {
    const mockFirestoreId = item.firestoreId || item.firestore_id
    if (!mockFirestoreId) {
      throw new Error(`Missing firestoreId for Supabase mock ref in collection '${collectionPath}'`)
    }
    return buildFirestoreRef(mockFirestoreId)
  }

  // Handle case where an item object with ref property is passed
  if (item.ref) {
    const ref = item.ref
    // Check if it's a real Firestore ref by looking for Firestore-specific properties
    if (ref._delegate || ref.firestore || ref._key || ref._converter !== undefined) {
      return item.ref
    }
    // Check if it's a mock Supabase ref
    if (ref._isSupabaseMock) {
      const refFirestoreId = ref.firestoreId || ref.firestore_id
      if (!refFirestoreId) {
        throw new Error(`Missing firestoreId for Supabase mock ref in collection '${collectionPath}'`)
      }
      return buildFirestoreRef(refFirestoreId)
    }
  }

  // Use firestoreId if available (Supabase-migrated data)
  // Support both camelCase (firestoreId) and snake_case (firestore_id)
  const firestoreId = item.firestoreId || item.firestore_id
  if (firestoreId) {
    return buildFirestoreRef(firestoreId)
  }

  // Fallback: use the id directly
  if (item.id) {
    return buildFirestoreRef(item.id)
  }

  // If it's already a Firestore ref (has the right properties), return it
  if (item._delegate || item.firestore || item._key || item._converter !== undefined) {
    return item
  }

  throw new Error(`Cannot resolve Firestore ref for collection '${collectionPath}': no valid id or ref found`)
}

/**
 * Commit a Firestore write batch, tolerating "document already deleted / not found"
 * errors. In Supabase mode the Firestore docs a delete/reversal batch touches often
 * don't exist (data lives in Supabase), so a bare `batch.commit()` throws "No document
 * to update" and aborts BEFORE the Supabase API cleanup runs — leaving the operation
 * un-reversed in Supabase. This swallows only that missing-doc class (re-throwing
 * everything else) so the caller can always proceed to its Supabase write.
 */
export const commitBatchTolerant = async (
  batch: { commit: () => Promise<unknown> },
  label: string,
): Promise<void> => {
  try {
    await batch.commit()
  } catch (firestoreError) {
    const msg = firestoreError instanceof Error ? firestoreError.message : String(firestoreError)
    if (msg.includes('No document to update') || msg.includes('NOT_FOUND')) {
      console.warn(
        `[${label}] Firestore batch failed (doc may already be deleted), proceeding to Supabase cleanup:`,
        msg,
      )
    } else {
      throw firestoreError
    }
  }
}

/**
 * Run a stock-reversing DELETE as an IDEMPOTENT Firestore transaction.
 *
 * Inventory delete-ops (deleteSupply/deleteWriteOff/deleteRevision/deleteTransfer) reverse
 * stock by deleting a primary doc AND applying storeStock increments + `movement` rows +
 * dailyStats decrements. A Firestore *batch* re-applies ALL of that on every commit, and
 * `batch.delete` of an already-deleted doc does NOT error — so a frozen UI, double-tap,
 * offline replay, or retry can run the reversal 2–3× and drive stock badly negative (at
 * store "Усач" / org VSH a supply delete fired 3× → a product's stock hit -645).
 *
 * This wraps the reversal in a transaction that first checks the primary doc still exists:
 *  - Already gone (retry / concurrent double-fire, or Supabase-mode where the Firestore doc
 *    was never written): the reversal is skipped entirely — a safe no-op — and the caller
 *    still proceeds to its Supabase cleanup. This is what makes the delete idempotent AND
 *    preserves the "clean up Supabase even when Firestore has nothing" guarantee that the old
 *    commitBatchTolerant path provided (the primary-absent case is exactly what it tolerated).
 *  - Exists: the primary delete + `applyWrites` reversal commit ATOMICALLY. Any failure (a
 *    missing dependent doc, permissions, offline) rolls the whole thing back AND propagates,
 *    so the caller does NOT reach its Supabase delete — Firestore and Supabase stay consistent
 *    (both keep the record) rather than diverging. We deliberately do NOT swallow a dependent
 *    "No document to update"/NOT_FOUND here: doing so while still deleting the row in Supabase
 *    would leave the record present in Firestore (the customer-facing read source) but gone in
 *    Supabase.
 *
 * `applyWrites(tx)` MUST perform only writes (`tx.set/update/delete`) — Firestore requires all
 * reads before writes, and the one existence read is already done here. The primary doc is
 * deleted for you before `applyWrites` runs.
 */
export const runIdempotentDelete = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- refs/tx are `any` throughout the inventory module (dynamic Firestore/mock refs)
  primaryRef: any,
  label: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tx is a Firestore transaction; callers write dotted field-paths on `any` refs
  applyWrites: (tx: any) => void,
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tx is a Firestore transaction
  await rootDb.runTransaction(async (tx: any) => {
    const snap = await tx.get(primaryRef)
    if (!snapshotExists(snap)) {
      // Primary doc already deleted (retry / concurrent double-fire) or never existed in
      // Firestore (Supabase-mode). The reversal was already applied once (or belongs to
      // Supabase only) — re-applying would double-subtract inventory. Safe no-op; the caller
      // still runs its Supabase cleanup afterwards.
      console.warn(`[${label}] primary doc already deleted; skipping duplicate stock reversal`)
      return
    }
    tx.delete(primaryRef)
    applyWrites(tx)
  })
}
